// Ekran 08'in sağ inceleme paneli — segmente göre farklı kart.
//
// Referansta segment ile panel türü SIZIYORDU: bakım panelindeki "Aracı aç"
// düğmesi tabloyu değiştirmeden araç kartı açıyor, tablo seçimi kayboluyordu.
// Burada ikisi bağımsız durum: `secim` panelin ne göstereceğini söylüyor.

import { tr, trBugun, trTarih, trTarihSaat } from '../../lib/trFormat'
import {
  aracRozeti, bakimRozeti, cezaEtiketi, dorseRozeti, gunAy, seferEtiketi, vadeHucresi,
} from './ortak'
import type { Arac, Bakim, Ceza, Dolum, Dorse, Evrak, Sefer, Surucu } from './api'

export type PanelSecimi =
  | { tur: 'yok' }
  | { tur: 'arac'; id: string }
  | { tur: 'dorse'; id: string }
  | { tur: 'surucu'; id: string }
  | { tur: 'bakim'; id: string }

function Satir({ etiket, deger }: { etiket: string; deger: React.ReactNode }) {
  return (
    <div className="mini-li">
      <span>{etiket}</span>
      <span className="vv">{deger}</span>
    </div>
  )
}

/** Kayıt yoksa referansın soluk tek satırı — bölüm yine de duruyor. */
function BosSatir({ metin }: { metin: string }) {
  return <div className="mini-li" style={{ color: 'var(--faint)' }}>{metin}</div>
}

export default function FiloPaneli({
  secim, araclar, dorseler, suruculer, evraklar, dolumlar, bakimlar, cezalar, seferler,
  yazabilir, aracAc, atamaDegis, ekYukleyici, bakimDurumDegis,
}: {
  secim: PanelSecimi
  araclar: Arac[]
  dorseler: Dorse[]
  suruculer: Surucu[]
  evraklar: Evrak[]
  dolumlar: Dolum[]
  bakimlar: Bakim[]
  cezalar: Ceza[]
  /** Program gününün seferleri — araç ve sürücü bölümlerini besliyor. */
  seferler: Sefer[]
  yazabilir: boolean
  aracAc: (id: string) => void
  /** Referanstaki `ata()` — select değişince anında kaydeder. */
  atamaDegis: (arac: Arac, g: { surucuId: string | null; dorseId: string | null }) => void
  /** Bakım kaydına panelden dosya ekler (POST /api/bakimlar/{id}/ekler). */
  ekYukleyici: (bakim: Bakim, dosyalar: File[]) => void
  bakimDurumDegis: (bakim: Bakim, durum: string) => void
}) {
  if (secim.tur === 'yok') {
    return <div className="insp-empty">Bir kayıt seçin.</div>
  }

  // ------------------------------------------------------------------ araç
  if (secim.tur === 'arac') {
    const a = araclar.find((x) => x.id === secim.id)
    if (!a) return <div className="insp-empty">Kayıt bulunamadı.</div>

    const [rozetSinif, rozetMetin] = aracRozeti(a.durum)
    const kendiEvraklari = evraklar.filter((e) => e.sahipTip === 'arac' && e.sahipId === a.id)
    const sonDolumlar = dolumlar.filter((d) => d.aracId === a.id).slice(0, 3)
    const sonBakimlar = bakimlar
      .filter((b) => b.sahipTip === 'arac' && b.sahipId === a.id).slice(0, 3)
    const aracSeferleri = seferler.filter((s) => s.aracId === a.id)

    // Referans yalnız boştaki sürücü/dorseyi listeliyor; aracın kendi ataması
    // listede kalmalı ki select seçili değerini bulabilsin.
    const bostaSuruculer = suruculer.filter(
      (s) => s.aracId === null || s.aracId === a.id || s.id === a.surucuId)
    const bostaDorseler = dorseler.filter(
      (d) => d.aracId === null || d.aracId === a.id || d.id === a.dorseId)
    // Damperli kamyon dorse çekmez (referans da select'i kapatıyor).
    const dorseTasimaz = a.tip?.includes('Kamyon') ?? false

    return (
      <>
        <div className="insp-h">
          <div className="insp-eyebrow">
            <span className="kod">{a.plaka}</span><span>·</span><span>{a.tip}</span>
          </div>
          <div className="insp-t">
            {a.model}
            <span className={rozetSinif}>{rozetMetin}</span>
          </div>
        </div>

        {/* Bölüm sırası referanstaki gibi: atamalar → maliyet → evrak →
            bakım → dolum → sefer. */}
        <div className="insp-b">
          <div className="insp-sec">
            <div className="insp-sec-t">Atamalar — araç ↔ dorse ↔ sürücü</div>
            <div className="fgrid">
              <div className="fld">
                <label>Sürücü</label>
                <select
                  className="in"
                  value={a.surucuId ?? ''}
                  disabled={!yazabilir}
                  onChange={(e) => atamaDegis(a, {
                    surucuId: e.target.value || null, dorseId: a.dorseId,
                  })}
                >
                  <option value="">— atanmadı</option>
                  {bostaSuruculer.map((s) => (
                    <option key={s.id} value={s.id}>{s.ad}</option>
                  ))}
                </select>
              </div>
              <div className="fld">
                <label>Dorse</label>
                <select
                  className="in"
                  value={a.dorseId ?? ''}
                  disabled={!yazabilir || dorseTasimaz}
                  title={dorseTasimaz ? 'Damperli kamyon dorse çekmez' : undefined}
                  onChange={(e) => atamaDegis(a, {
                    surucuId: a.surucuId, dorseId: e.target.value || null,
                  })}
                >
                  <option value="">— yok</option>
                  {/* K35: muayenesi geçmiş dorse görünür ama seçilemez —
                      operatör nedenini görsün (bkz. docs/kararlar-ek.md). */}
                  {bostaDorseler.map((d) => (
                    <option key={d.id} value={d.id} disabled={d.muayene.durum === 'gecmis'}>
                      {d.plaka} · {d.uygunluk === 'sivi' ? 'Sıvı' : 'Katı'}
                      {d.muayene.durum === 'gecmis' ? ' — muayene geçti' : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="insp-sec">
            <div className="inst">
              <div className="inst-t">Maliyet — Bu Ay</div>
              <div className="inst-3">
                <div className="inst-c">
                  <div className="inst-v">{tr(a.ay.tlKm, 2)}</div>
                  <div className="inst-l">TL/km toplam</div>
                </div>
                <div className="inst-c">
                  <div className="inst-v">{tr(a.ay.l100, 1)}</div>
                  <div className="inst-l">L/100 km</div>
                </div>
                <div className="inst-c">
                  <div className="inst-v">{tr(a.ay.km, 0)}</div>
                  <div className="inst-l">km/ay</div>
                </div>
              </div>
              <div className="inst-row">
                <span className="inst-l">Yakıt (SK: Motorin)</span>
                <span className="inst-v">{tr(a.ay.yakitTl, 0)} ₺</span>
              </div>
              <div className="inst-row">
                <span className="inst-l">Bakım — yalnız tamamlanmış (K38)</span>
                <span className="inst-v">{tr(a.ay.bakimTl, 0)} ₺</span>
              </div>
              <div className="inst-row">
                <span className="inst-l">Lastik</span>
                <span className="inst-v">{tr(a.ay.lastikTl, 0)} ₺</span>
              </div>
            </div>
          </div>

          <div className="insp-sec">
            <div className="insp-sec-t">Evrak Takibi ({kendiEvraklari.length})</div>
            <div className="mini-list">
              {kendiEvraklari.length === 0
                ? <BosSatir metin="Evrak kaydı yok — uygunluk doğrulanamıyor." />
                : kendiEvraklari.map((e) => {
                    const [sinif, metin] = vadeHucresi(e.vade)
                    return (
                      <div className="mini-li" key={e.id}>
                        <span>{e.tur}</span>
                        <span className={`vv ${sinif}`}>{metin}</span>
                      </div>
                    )
                  })}
            </div>
          </div>

          <div className="insp-sec">
            {/* Referans başlığı "Ağustos" yazıyordu; uç ayı süzmüyor, o yüzden
                başlık kaydın kapsamını söylüyor. */}
            <div className="insp-sec-t">Bakım &amp; Onarım — son kayıtlar</div>
            <div className="mini-list">
              {sonBakimlar.length === 0
                ? <BosSatir metin="Kayıt yok" />
                : sonBakimlar.map((b2) => (
                    <div className="mini-li" key={b2.id}>
                      <span className="dt">{gunAy(b2.tarih)}</span>
                      <span>{b2.tur ?? '—'}{b2.ekler.length > 0 ? ` · 📎${b2.ekler.length}` : ''}</span>
                      <span className="vv">
                        {tr(b2.tutar, 0)} ₺ · {bakimRozeti(b2.durum)[1]}
                      </span>
                    </div>
                  ))}
            </div>
          </div>

          <div className="insp-sec">
            <div className="insp-sec-t">Son Dolumlar</div>
            <div className="mini-list">
              {sonDolumlar.length === 0
                ? <BosSatir metin="Dolum yok" />
                : sonDolumlar.map((d) => (
                    <div className="mini-li" key={d.id}>
                      <span className="dt">{gunAy(d.zaman)}</span>
                      <span>{tr(d.litre, 0)} L · {d.istasyon ?? '—'}</span>
                      <span className="vv">
                        {d.aralikL100 === null ? '—' : `${tr(d.aralikL100, 1)} L/100`}
                      </span>
                    </div>
                  ))}
            </div>
          </div>

          <div className="insp-sec">
            <div className="insp-sec-t">Bugünkü Seferleri</div>
            <div className="mini-list">
              {aracSeferleri.length === 0
                ? <BosSatir metin="Sefer yok" />
                : aracSeferleri.map((s) => (
                    <div className="mini-li" key={s.id}>
                      <span className="dt">{s.saat?.slice(0, 5) ?? '—'}</span>
                      <span>{s.cariUnvan ?? '—'} · {s.stokAd ?? '—'}</span>
                      <span className="vv">{seferEtiketi(s.durum)}</span>
                    </div>
                  ))}
            </div>
          </div>
        </div>

        <div className="insp-f">
          {/* Atamalar anında yazılıyor, panelde serbest alan yok — referansın
              Kaydet'i yalnız toast basıyordu; sahte toast yerine kapalı düğme. */}
          <button
            className="btn btn-pri btn-sm"
            style={{ flex: 1, justifyContent: 'center' }}
            type="button"
            disabled
            title="Panelde bekleyen değişiklik yok — atamalar seçildiği anda kaydediliyor"
          >
            Kaydet
          </button>
        </div>
      </>
    )
  }

  // ----------------------------------------------------------------- dorse
  if (secim.tur === 'dorse') {
    const d = dorseler.find((x) => x.id === secim.id)
    if (!d) return <div className="insp-empty">Kayıt bulunamadı.</div>

    const [uygSinif, uygMetin] = dorseRozeti(d.uygunluk)
    const [vSinif, vMetin] = vadeHucresi(d.muayene)

    return (
      <>
        <div className="insp-h">
          <div className="insp-eyebrow">
            <span className="kod">{d.plaka}</span><span>·</span><span>Dorse</span>
          </div>
          <div className="insp-t">
            {d.tip}
            <span className={uygSinif}>{uygMetin}</span>
          </div>
        </div>

        <div className="insp-b">
          {d.muayene.durum === 'gecmis' && (
            <div className="insp-sec">
              <div
                className="info-box"
                style={{ background: 'var(--red-bg)', borderColor: 'var(--red-bd)', color: 'var(--red)', marginBottom: 0 }}
              >
                <div>⚠ Muayene süresi geçmiş — sefere atanamaz (K35).</div>
              </div>
            </div>
          )}
          <div className="insp-sec">
            <div className="insp-sec-t">Bilgiler</div>
            <div className="mini-list">
              <Satir etiket="Hacim" deger={d.hacim === null ? '—' : `${tr(d.hacim, 0)} m³`} />
              {/* Sıra referanstaki gibi: bağlı çekici → muayene. */}
              <Satir etiket="Bağlı çekici" deger={d.aracPlaka ?? 'boşta'} />
              <Satir etiket="Muayene" deger={<span className={vSinif}>{vMetin}</span>} />
            </div>
          </div>
        </div>
      </>
    )
  }

  // ---------------------------------------------------------------- sürücü
  if (secim.tur === 'surucu') {
    const s = suruculer.find((x) => x.id === secim.id)
    if (!s) return <div className="insp-empty">Kayıt bulunamadı.</div>

    const belgeleri = evraklar.filter((e) => e.sahipTip === 'surucu' && e.sahipId === s.id)
    const arac = araclar.find((a) => a.id === s.aracId)
    const gorevler = seferler.filter((x) => x.surucuId === s.id)
    const bugunT = gorevler.reduce((t, x) => t + (x.gercekT ?? 0), 0)
    const tamamlanan = gorevler.filter((x) => x.durum === 'tamamlandi').length
    const surucuCezalari = cezalar.filter((c) => c.surucuId === s.id)

    // Başlık "12 ay" diyor, sayı da 12 ayla sınırlanıyor: uç tüm cezaları döndürüyor.
    const bugun = trBugun()
    const birYilOnce = `${Number(bugun.slice(0, 4)) - 1}${bugun.slice(4)}`
    const sonYilIhlal = surucuCezalari.filter((c) => c.tarih >= birYilOnce).length

    // Referans surYakit(): sürücünün aralıklı dolumlarında Σ litre / Σ km × 100.
    // Aracın ay ortalaması taban; sapma K37 bantlarıyla değil sürücü karnesiyle
    // (>%3 amber, >%10 kırmızı) renkleniyor — referanstaki eşikler.
    const araliklar = dolumlar.filter((d) => d.surucuId === s.id && d.aralikKm)
    const surucuKm = araliklar.reduce((t, d) => t + (d.aralikKm ?? 0), 0)
    const surucuL100 = surucuKm > 0
      ? (araliklar.reduce((t, d) => t + d.litre, 0) / surucuKm) * 100
      : null
    const taban = arac?.ay.l100 ?? null
    const yakitFarki = surucuL100 !== null && taban ? ((surucuL100 - taban) / taban) * 100 : null
    const yakitSinifi = yakitFarki === null ? '' : yakitFarki > 10 ? ' red' : yakitFarki > 3 ? ' amber' : ''

    return (
      <>
        <div className="insp-h">
          <div className="insp-eyebrow">
            <span>Sürücü</span><span>·</span><span>{s.tel ?? '—'}</span>
          </div>
          <div className="insp-t">{s.ad}</div>
        </div>

        <div className="insp-b">
          <div className="insp-sec">
            <div className="inst">
              <div className="inst-t">Performans</div>
              <div className="inst-3">
                <div className="inst-c">
                  <div className="inst-v">{tr(s.puan, 0)}</div>
                  <div className="inst-l">puan / 100</div>
                </div>
                <div className="inst-c">
                  <div className="inst-v">{tr(bugunT, 1)}</div>
                  <div className="inst-l">bugün t</div>
                </div>
                <div className="inst-c">
                  <div className="inst-v">{tamamlanan}/{gorevler.length}</div>
                  <div className="inst-l">bugün sefer</div>
                </div>
              </div>
              {/* Referans burada "Σ plan × 22" ile aylık tonaj UYDURUYORDU;
                  taşınan tonaj yalnız gün için biliniyor, o da yukarıda. */}
              <div className="inst-row">
                <span className="inst-l">Bu ay km ({arac?.plaka ?? 'araç yok'})</span>
                <span className="inst-v">{arac ? `${tr(arac.ay.km, 0)} km` : '—'}</span>
              </div>
              <div className="inst-row">
                <span className="inst-l">İhlal (12 ay)</span>
                <span className={`inst-v${sonYilIhlal > 0 ? ' amber' : ''}`}>
                  {sonYilIhlal} ceza{s.acikCeza > 0 ? ` · ${s.acikCeza} açık` : ''}
                </span>
              </div>
              {surucuL100 !== null && (
                <div className="inst-row">
                  <span className="inst-l">Yakıt verimliliği</span>
                  <span className={`inst-v${yakitSinifi}`}>
                    {tr(surucuL100, 1)} L/100
                    {yakitFarki !== null
                      && ` · ${yakitFarki > 0 ? '+' : ''}${tr(yakitFarki, 1)}% ${yakitFarki > 3 ? 'araç ort. üstü' : 'tasarruflu'}`}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="insp-sec">
            <div className="insp-sec-t">Belge Takibi ({belgeleri.length})</div>
            <div className="mini-list">
              {belgeleri.length === 0
                ? <BosSatir metin="Belge kaydı yok." />
                : belgeleri.map((e) => {
                    const [sinif, metin] = vadeHucresi(e.vade)
                    return (
                      <div className="mini-li" key={e.id}>
                        <span>{e.tur}</span>
                        <span className={`vv ${sinif}`}>{metin}</span>
                      </div>
                    )
                  })}
            </div>
          </div>

          <div className="insp-sec">
            {/* Referanstaki "iletildi" rozeti gönderilmemiş bir raporu iletilmiş
                gösteriyordu; rozet gerçek sayıyı yazıyor. */}
            <div className="insp-sec-t">
              Bugünün Görev Raporu
              {gorevler.length > 0 && (
                <span className="badge stat-plan" style={{ marginLeft: 5 }}>
                  {tamamlanan}/{gorevler.length} sefer
                </span>
              )}
            </div>
            <div className="mini-list">
              {gorevler.length === 0
                ? <BosSatir metin="Görev atanmadı" />
                : gorevler.map((x) => (
                    <div className="mini-li" key={x.id}>
                      <span className="dt">{x.saat?.slice(0, 5) ?? '—'}</span>
                      <span>{x.cariUnvan ?? '—'}</span>
                      <span className="vv">{x.stokAd ?? '—'} · {tr(x.planT, 1)} t</span>
                    </div>
                  ))}
            </div>
          </div>

          <div className="insp-sec">
            <div className="insp-sec-t">Cezalar</div>
            <div className="mini-list">
              {surucuCezalari.length === 0
                ? <BosSatir metin="Kayıt yok" />
                : surucuCezalari.map((c) => (
                    <div className="mini-li" key={c.id}>
                      <span className="dt">{gunAy(c.tarih)}</span>
                      <span>{c.madde ?? '—'}</span>
                      <span className="vv">{tr(c.tutar, 0)} ₺ · {cezaEtiketi(c.durum)}</span>
                    </div>
                  ))}
            </div>
          </div>
        </div>

        <div className="insp-f">
          {/* Görev raporu gönderimi (SMS / uygulama) motorda yok. Referans burada
              yan etkisiz toast basıyordu — düğme kapalı, nedeni title'da. */}
          <button
            className="btn btn-sec btn-sm btn-block"
            type="button"
            disabled
            title="Görev raporu gönderimi (SMS / uygulama) henüz bağlı değil"
          >
            Raporu gönder
          </button>
        </div>
      </>
    )
  }

  // ----------------------------------------------------------------- bakım
  const b = bakimlar.find((x) => x.id === secim.id)
  if (!b) return <div className="insp-empty">Kayıt bulunamadı.</div>

  const [bSinif, bMetin] = bakimRozeti(b.durum)
  const sahipArac = b.sahipTip === 'arac' ? araclar.find((a) => a.id === b.sahipId) : undefined

  return (
    <>
      <div className="insp-h">
        <div className="insp-eyebrow">
          <span className="kod">{b.sahipPlaka}</span><span>·</span><span>{trTarih(b.tarih)}</span>
        </div>
        <div className="insp-t">
          {b.tur}
          <span className={bSinif}>{bMetin}</span>
        </div>
        {/* Açıklama referansta başlığın altında duruyor — aşağıdaki mini
            listede tekrarlanmıyor. */}
        <div className="cell-sub" style={{ marginTop: 2 }}>{b.aciklama ?? '—'}</div>
      </div>

      <div className="insp-b">
        <div className="insp-sec">
          <div className="insp-sec-t">Kayıt</div>
          <div className="mini-list">
            <Satir etiket="Servis" deger={b.servis ?? '—'} />
            <Satir etiket="Sayaç" deger={b.sayacKm === null ? '—' : `${tr(b.sayacKm, 0)} km`} />
            <Satir etiket="Tutar" deger={`${tr(b.tutar, 0)} ₺`} />
            {/* Referansta durum da bu listenin bir satırı; K38 dipnotu artık
                bölümün altındaki `.pnote`'ta duruyor. */}
            <Satir
              etiket="Durum"
              deger={(
                <select
                  className="in"
                  style={{ padding: '2px 5px', fontSize: 10.5 }}
                  value={b.durum}
                  disabled={!yazabilir}
                  onChange={(e) => bakimDurumDegis(b, e.target.value)}
                >
                  <option value="planlandi">Planlandı</option>
                  <option value="serviste">Serviste</option>
                  <option value="tamamlandi">Tamamlandı</option>
                </select>
              )}
            />
          </div>
        </div>

        <div className="insp-sec">
          <div className="insp-sec-t">Ekler — evrak &amp; fotoğraf ({b.ekler.length})</div>
          <div style={{ padding: '2px 0 6px' }}>
            {b.ekler.length === 0
              ? <span className="cell-sub">Henüz dosya yok</span>
              : b.ekler.map((e) => (
                  <a className="ek" key={e.id} href={e.yol} target="_blank" rel="noreferrer">
                    {e.mime?.startsWith('image/') && <img src={e.yol} alt="" />}
                    {e.dosyaAdi}
                  </a>
                ))}
          </div>
          {yazabilir && (
            <label className="up" style={{ display: 'block' }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" />
              </svg>
              Evrak / resim yükle — PDF, JPG, PNG
              <input
                type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp" style={{ display: 'none' }}
                onChange={(e) => {
                  const dosyalar = Array.from(e.target.files ?? [])
                  // Aynı dosya ikinci kez seçilebilsin diye input sıfırlanıyor.
                  e.target.value = ''
                  if (dosyalar.length > 0) ekYukleyici(b, dosyalar)
                }}
              />
            </label>
          )}
        </div>

        <div className="insp-sec">
          <div className="pnote" style={{ margin: 0 }}>
            Tamamlanan tutarlar aracın <b>TL/km</b> maliyetine (bakım/lastik kalemi)
            otomatik işlenir; ekler evrak arşivinde saklanır.
          </div>
        </div>
      </div>

      <div className="insp-f">
        {sahipArac && (
          <button
            className="btn btn-sec btn-sm"
            style={{ padding: '0 10px' }}
            type="button"
            onClick={() => aracAc(sahipArac.id)}
          >
            Aracı aç
          </button>
        )}
        {/* Durum ve ekler anında yazılıyor; panelde bekleyen bir düzenleme yok.
            Referansın Kaydet'i yalnız toast basıyordu — kapalı, nedeni title'da. */}
        <button
          className="btn btn-pri btn-sm"
          style={{ flex: 1, justifyContent: 'center' }}
          type="button"
          disabled
          title="Panelde bekleyen değişiklik yok — durum ve ekler seçildiği anda kaydediliyor"
        >
          Kaydet
        </button>
      </div>
    </>
  )
}

export { trTarihSaat }
